/* global completeHeight */

let lastHeaderHeight = null,
    menuFixedUpdateQueued = false;

/**
 * Adds/removes the `.nav-scrolled` class on the header once the page scrolls past the header's
 * own height, compensating the main content's top padding to avoid a layout jump. Also keeps the
 * `--header-height` CSS variable up to date, used by global.scss's `scroll-margin-top` so that
 * scrolling to a focused/target element never leaves it hidden behind the fixed header
 * (WCAG 2.4.11 Focus Not Obscured). Only touches the DOM when something actually changed — the
 * variable lives on `<html>`, so rewriting it on every scroll event would invalidate the style of
 * the whole document.
 * @returns {void}
 */
function menuFixedOnScroll() {
    const nav = document.querySelector('#header'),
        main = document.querySelector('main'),
        navHeight = completeHeight(nav),
        windowTop = window.pageYOffset || document.body.scrollTop,
        scrolled = windowTop > navHeight;

    if (scrolled !== nav.classList.contains('nav-scrolled')) {
        main.style.paddingTop = scrolled ? `${navHeight}px` : 0;
        nav.classList.toggle('nav-scrolled', scrolled);
    }

    // offsetHeight (sem as margens) porque a animação `menu-fixed` mexe no margin-top do header fixado
    const height = nav.offsetHeight;
    if (height !== lastHeaderHeight) {
        lastHeaderHeight = height;
        document.documentElement.style.setProperty('--header-height', `${height}px`);
    }
}

/**
 * Schedules a single menuFixedOnScroll() run for the next animation frame, so a burst of scroll
 * events costs one layout read/write per frame instead of one per event.
 * @returns {void}
 */
function queueMenuFixedUpdate() {
    if (menuFixedUpdateQueued) {
        return;
    }
    menuFixedUpdateQueued = true;
    window.requestAnimationFrame(() => {
        menuFixedUpdateQueued = false;
        menuFixedOnScroll();
    });
}

window.addEventListener('load', menuFixedOnScroll);
window.addEventListener('scroll', queueMenuFixedUpdate, { passive: true });

/**
 * Moves keyboard focus to the page's main content, used by the "skip to content" link.
 * @returns {void}
 */
// eslint-disable-next-line
function skipLinkMain() {
    document.getElementsByTagName('main')[0].focus();
}

/**
 * Moves keyboard focus to the given element when triggered by the Enter or Space key, used by
 * elements that need to defer focus until after their own keydown handling finishes (e.g. the
 * "back to top" button, whose native button activation would otherwise leave focus on an element
 * that becomes `visibility: hidden` once the page scrolls back up).
 * @param {HTMLElement} element Element to focus.
 * @returns {void}
 */
// eslint-disable-next-line
function skipLink(element) {
    if (window.event.code === 'Enter' || window.event.code === 'Space') {
        setTimeout(() => {
            element.focus();
        }, 50);
    }
}

/**
 * Registers the global keyboard shortcut AltGr+I, which goes to the home page through the
 * header's logo link (`#s-inicial`).
 * @returns {void}
 */
function initAccessibilityShortcuts() {
    document.addEventListener('keydown', (event) => {
        if (event.getModifierState('AltGraph') && event.key.toLowerCase() === 'i') {
            document.querySelector('#s-inicial')?.click();
        }
    });
}

initAccessibilityShortcuts();

/**
 * Works around a Bootstrap modal bug where opening a modal shifts the fixed header: keeps the
 * header's width fixed while any `.modal` is open. Deferred to DOMContentLoaded because the
 * `.modal` elements (e.g. cookies.php) only exist further down the document.
 * @returns {void}
 */
function fixModalHeaderWidth() {
    const nb = document.querySelector('#header'),
        modals = document.querySelectorAll('.modal');

    modals.forEach(e => {
        e.addEventListener('show.bs.modal', () => {
            nb.style.width = `${nb.offsetWidth}px`;
        });
        e.addEventListener('hidden.bs.modal', () => {
            nb.style.width = 'auto';
        });
    });
}

document.addEventListener('DOMContentLoaded', fixModalHeaderWidth);

/**
 * Opens or closes the mobile menu panel, keeping the toggle button's `aria-expanded` and
 * `aria-label` in sync with the panel's real state.
 * @param {boolean} open True to open the panel, false to close it.
 * @returns {void}
 */
function setMenuOpen(open) {
    const toggle = document.querySelector('#menu-toggle'),
        menu = document.querySelector('#menu');

    toggle.setAttribute('aria-expanded', String(open));
    toggle.setAttribute('aria-label', open ? 'Fechar menu' : 'Abrir menu');
    menu.classList.toggle('is-open', open);
}

/**
 * Wires the mobile menu: the hamburger button toggles the panel; the panel closes when a link is
 * chosen, on Escape (returning focus to the button), on a click outside the header, and when the
 * viewport grows past the desktop breakpoint (where the menu is always visible).
 * @returns {void}
 */
function initMenuToggle() {
    const toggle = document.querySelector('#menu-toggle'),
        menu = document.querySelector('#menu'),
        header = document.querySelector('#header');

    if (toggle === null || menu === null) { return; }

    toggle.addEventListener('click', () => {
        setMenuOpen(toggle.getAttribute('aria-expanded') !== 'true');
    });

    menu.addEventListener('click', e => {
        if (e.target.closest('a')) { setMenuOpen(false); }
    });

    document.addEventListener('keydown', e => {
        if (e.key === 'Escape' && toggle.getAttribute('aria-expanded') === 'true') {
            setMenuOpen(false);
            toggle.focus();
        }
    });

    document.addEventListener('click', e => {
        if (!header.contains(e.target)) { setMenuOpen(false); }
    });

    window.matchMedia('(min-width: 1200px)').addEventListener('change', e => {
        if (e.matches) { setMenuOpen(false); }
    });
}

initMenuToggle();